import React, { Component } from 'react';
import PropTypes from 'prop-types';
import chroma from 'chroma-js';
import { Bert } from 'meteor/themeteorchef:bert';
import { Col } from 'react-bootstrap';
import { isEmpty, isEqual, cloneDeep, isNumber, findIndex, set, isBoolean } from 'lodash';

import { SYMBOLOGY_TYPES } from '../../../api/Styles/Styles';
import {
  isCategorizedSimbology,
  getColorRange,
  getLegend
} from '../../../api/Styles/style-operations';

import { parseFeaturePropertyValues } from '../../../modules/parse-geojson-properties';
import handleErrorInClient from '../../../modules/handle-error-in-client';

import ColorRangesField from '../../atoms/ColorRangesField';
import StyleOpacityField from '../../atoms/StyleOpacityField';
import GeoJSONAttributesField from '../../atoms/GeoJSONAttributesField';
import GeometryTypeField from '../../atoms/GeometryTypeField';
import GeometryPointFields from '../../atoms/GeometryPointFields';
import GeometryOutlineFields from '../../atoms/GeometryOutlineFields';
import SymbologyDataPreviewTable from '../SymbologyDataPreviewTable';

const EMPTY_VALUE = 'read_as_empty_value';
const CUSTOM = 'custom';

class CategorizedFields extends Component {
  state = {
    isCustom: this.props.colorRange === CUSTOM
  };

  componentDidUpdate(prevProps) {
    const { symbologyType: prevSimbologyType, colorRange: prevColorRange } = prevProps;
    const { symbologyType, updateLegend, style, colorRange } = this.props;
    if (
      symbologyType === SYMBOLOGY_TYPES.CATEGORIZED &&
      !isEqual(prevSimbologyType, symbologyType) &&
      !isCategorizedSimbology(style)
    )
      updateLegend([], 0.9);

    if (colorRange !== prevColorRange) this.updateCustomStatus(colorRange);
  }

  changeColorScale = event => {
    const { legend, style, propName } = this.props;
    const newColorRange = event.target.value;
    const alpha = Number(event.target.form.alphaSelector.value);
    const actualPropName = event.target.form.propName.value.trim();
    this.changeCustomStatus(newColorRange === CUSTOM);
    if (getColorRange(style) === CUSTOM && newColorRange === CUSTOM && propName === actualPropName)
      this.reColorLegend(getLegend(style), newColorRange, alpha);
    else this.reColorLegend(legend, newColorRange === CUSTOM ? ['white'] : newColorRange, alpha);
  };

  changeCustomLegend = (index, rgbColor) => {
    const { legend, alpha } = this.props;
    const newLegendCount = cloneDeep(legend);
    const arrayIndex = findIndex(newLegendCount, ['index', index]);
    newLegendCount[arrayIndex].color = rgbColor.hex;
    this.reColorLegend(newLegendCount, CUSTOM, alpha);
  };

  changeCustomStatus = newCustomStatus => this.setState({ isCustom: newCustomStatus });

  changeSelectedProperty = event => {
    const { geojson, propName, style } = this.props;
    const newPropName = event.target.value;
    const colorRange = event.target.form.colorRangeValues.value;
    const alpha = Number(event.target.form.alphaSelector.value);

    if (!this.canYouCalculateNewLegend(geojson, newPropName))
      // eslint-disable-next-line no-param-reassign
      event.target.value = event.target.defaultValue;

    if (propName !== newPropName) this.setState({ isCustom: false });

    try {
      let legendToUse = getLegend(style);
      let colorRangeToUse = colorRange;
      if (getColorRange(style) !== CUSTOM || colorRange !== CUSTOM || propName !== newPropName) {
        legendToUse = parseFeaturePropertyValues(geojson, newPropName);
        colorRangeToUse = colorRange === CUSTOM ? ['white'] : colorRange;
      }

      this.reColorLegend(legendToUse, colorRangeToUse, alpha);
    } catch (error) {
      handleErrorInClient(error);
      set(event, ['target', 'value'], '');
    }
  };

  updateAlpha = event => {
    const { legend } = this.props;
    const alpha = Number(event.target.value);
    const colorRange = event.target.form.colorRangeValues.value;
    this.reColorLegend(legend, colorRange, alpha);
  };

  updateCustomStatus = colorRange => this.setState({ isCustom: colorRange === CUSTOM });

  assignNewColorsToLegend(newColorRange, legendCount) {
    const newLegendCount = cloneDeep(legendCount);
    if (!isEmpty(newLegendCount)) {
      const colorScale = chroma.scale(newColorRange).colors(legendCount.length);
      for (let index = 0; index < newLegendCount.length; index += 1) {
        const element = newLegendCount[index];
        if (
          (!isNumber(element.value) && isEmpty(element.value) && !isBoolean(element.value)) ||
          element.value === EMPTY_VALUE
        ) {
          element.value = EMPTY_VALUE;
          element.color = '#fff';
        } else {
          element.color = colorScale[index];
        }
      }
    }
    return newLegendCount;
  }

  canYouCalculateNewLegend(geojson, propName) {
    if (isEmpty(geojson)) {
      Bert.alert('You need to reload the geojson', 'danger');
      return false;
    }
    if (isEmpty(propName)) {
      Bert.alert('You need to select a property', 'danger');
      return false;
    }
    return true;
  }

  reColorLegend(legend, colorRange, alpha) {
    const { updateLegend } = this.props;
    let newLegend = legend;
    if (!isEmpty(colorRange) && colorRange !== CUSTOM)
      newLegend = this.assignNewColorsToLegend(colorRange, legend);
    updateLegend(newLegend, alpha);
  }

  render() {
    const {
      symbologyType,
      geojsonProperties,
      geojsonGeometry,
      legend,
      colorRange,
      propName,
      alpha,
      style,
      hasTolerance,
      updateBorderColor,
      borderAlpha,
      borderColor,
      pointType,
      changePointType,
      svgCategoryList
    } = this.props;

    if (symbologyType === SYMBOLOGY_TYPES.CATEGORIZED) {
      const { isCustom } = this.state;
      return (
        <>
          <Col xs={12} sm={6} md={12} lg={6}>
            <GeometryTypeField geojsonGeometry={geojsonGeometry} />
            <GeoJSONAttributesField
              propName={isEmpty(legend) ? '' : propName}
              changeSelectedProperty={this.changeSelectedProperty}
              geojsonProperties={geojsonProperties}
              symbologyType={symbologyType}
            />
            <GeometryPointFields
              geojsonGeometry={geojsonGeometry}
              style={style}
              hasTolerance={hasTolerance}
              pointType={pointType}
              changePointType={changePointType}
              svgCategoryList={svgCategoryList}
            />
            <GeometryOutlineFields
              updateBorderColor={updateBorderColor}
              borderAlpha={borderAlpha}
              borderColor={borderColor}
              geojsonGeometry={geojsonGeometry}
              style={style}
            />
            <br />
            <ColorRangesField
              changeColorScale={this.changeColorScale}
              colorRange={isEmpty(legend) ? '' : colorRange}
              symbologyType={symbologyType}
              isCustom={isCustom}
            />
            <StyleOpacityField alpha={alpha} updateAlpha={this.updateAlpha} />
          </Col>
          <Col xs={12} sm={6} md={12} lg={6}>
            <SymbologyDataPreviewTable
              symbologyType={symbologyType}
              legend={legend}
              alpha={alpha}
              changeCustomStatus={this.changeCustomStatus}
              changeCustomLegend={this.changeCustomLegend}
            />
          </Col>
        </>
      );
    }
    return '';
  }
}

CategorizedFields.propTypes = {
  geojson: PropTypes.object.isRequired,
  alpha: PropTypes.number.isRequired,
  geojsonGeometry: PropTypes.string.isRequired,
  geojsonProperties: PropTypes.array.isRequired,
  legend: PropTypes.array.isRequired,
  updateLegend: PropTypes.func.isRequired,
  colorRange: PropTypes.string.isRequired,
  propName: PropTypes.string.isRequired,
  symbologyType: PropTypes.string.isRequired,
  style: PropTypes.object.isRequired,
  hasTolerance: PropTypes.bool.isRequired,
  updateBorderColor: PropTypes.func.isRequired,
  borderAlpha: PropTypes.number.isRequired,
  borderColor: PropTypes.string.isRequired,
  pointType: PropTypes.string.isRequired,
  changePointType: PropTypes.func.isRequired,
  svgCategoryList: PropTypes.array.isRequired
};

export default CategorizedFields;
